import { useEffect, useState } from 'react';
import { adminSecurityApi, AdminApiError, type AdminChallengeOption } from '../../api/admin.ts';
import { requestAssertion, wasDismissed } from '../../lib/webauthn.ts';
import { Alert, Button, Checkbox, TextInput } from '../ui.tsx';

/**
 * The second leg of an operator sign-in.
 *
 * Google has said which mailbox this is; it has not said who is holding the
 * browser, and on the console that can execute a payout batch that is the
 * question worth answering. The OAuth callback issues NO session when a factor
 * is owed — it leaves a short-lived partial-login cookie and redirects here,
 * so between the two legs there is nothing an attacker can steal that grants
 * anything.
 *
 * Nothing on this screen names the operator or lists what they enrolled beyond
 * a label, for the same reason the tenant challenge screen is spare.
 */
export default function AdminVerifyForm({ onVerified }: { onVerified: () => void }) {
  const [options, setOptions] = useState<AdminChallengeOption[] | null>(null);
  const [trustedDeviceDays, setTrustedDeviceDays] = useState(0);
  const [selected, setSelected] = useState<AdminChallengeOption | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  async function finishWithKey(id: string, publicKey: Parameters<typeof requestAssertion>[0]) {
    const assertion = await requestAssertion(publicKey);
    // A dismissed prompt is a choice, not a failure. The challenge stays live
    // so the person can simply press the button again.
    if (!assertion) return;
    await adminSecurityApi.verify({ challengeId: id, assertion, rememberDevice });
    onVerified();
  }

  async function start(option: AdminChallengeOption) {
    if (busy) return;
    setSelected(option);
    setCode('');
    setError(null);
    setBusy(true);
    try {
      const started = await adminSecurityApi.challenge({ type: option.type, methodId: option.methodId });
      setChallengeId(started.challengeId);
      // A security key has nothing to type: the challenge and the prompt are
      // one step, so it runs here rather than waiting for a submit button that
      // would have no input to submit.
      if (option.type === 'webauthn' && started.publicKey) {
        await finishWithKey(started.challengeId, started.publicKey);
      }
    } catch (caught) {
      if (!wasDismissed(caught)) {
        setError(caught instanceof AdminApiError ? caught.message : 'That did not work. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (busy || !challengeId) return;
    setBusy(true);
    setError(null);
    try {
      await adminSecurityApi.verify({ challengeId, code, rememberDevice });
      onVerified();
    } catch (caught) {
      // The challenge is deliberately NOT cleared: a mistyped digit must not
      // strand somebody on a screen with no way forward and no way back.
      setCode('');
      setError(caught instanceof AdminApiError ? caught.message : 'That code is not right.');
    } finally {
      setBusy(false);
    }
  }

  // What the redirect left behind. The partial token itself never reaches this
  // code — it stays in a cookie the browser cannot read, and the server
  // substitutes it on the challenge and verify calls.
  useEffect(() => {
    let canceled = false;
    adminSecurityApi
      .pending()
      .then((pending) => {
        if (canceled) return;
        if (!pending.pending || !pending.options?.length) {
          setExpired(true);
          return;
        }
        setOptions(pending.options);
        setTrustedDeviceDays(pending.trustedDeviceDays ?? 0);
        // Marking the first option checked is not the same as ASKING for it.
        // Challenges were started only by the radio's `onChange`, which does
        // not fire when an already-checked radio is clicked — so an operator
        // with exactly one option (one TOTP factor, recovery codes spent) got
        // a checked radio, no code field, no button, and no way to finish
        // signing in at all.
        const first = pending.options[0];
        if (first) void start(first);
      })
      .catch(() => {
        if (!canceled) setExpired(true);
      });
    return () => {
      canceled = true;
    };
  }, []);

  if (expired) {
    return (
      <div className="space-y-4">
        <Alert tone="error">That sign-in attempt expired. Please start again.</Alert>
        <a href="/api/admin/auth/google/start" className="btn btn-primary w-full">
          Sign in with Google
        </a>
      </div>
    );
  }

  if (!options) return <p className="text-sm text-[color:var(--color-muted)]">Checking…</p>;

  return (
    <div className="space-y-4">
      <p className="text-[color:var(--color-muted)]">Verify it&rsquo;s you to finish signing in.</p>

      {error ? <Alert tone="error">{error}</Alert> : null}

      <fieldset className="space-y-1">
        <legend className="text-sm font-medium">How would you like to verify?</legend>
        {options.map((option) => (
          <label key={`${option.type}:${option.methodId ?? 'none'}`} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="admin-mfa-method"
              checked={selected?.methodId === option.methodId && selected?.type === option.type}
              onChange={() => void start(option)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>

      {challengeId && selected?.type !== 'webauthn' ? (
        <TextInput
          id="admin-mfa-code"
          label="Code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          // A recovery code is `ABCD-EFGH-IJKL`, so a numeric keyboard would
          // make the credential of last resort the one that cannot be typed.
          inputMode={selected?.type === 'recovery' ? 'text' : 'numeric'}
          autoComplete={selected?.type === 'recovery' ? 'off' : 'one-time-code'}
          autoFocus
        />
      ) : null}

      {trustedDeviceDays > 0 ? (
        <Checkbox
          label={`Remember this browser for ${trustedDeviceDays} days`}
          hint="Skips this step at sign-in. It never replaces the check in front of a payout."
          checked={rememberDevice}
          onChange={(event) => setRememberDevice(event.target.checked)}
        />
      ) : null}

      {challengeId && selected?.type !== 'webauthn' ? (
        <Button type="button" className="w-full" onClick={() => void submit()} disabled={busy || code.length === 0}>
          Verify
        </Button>
      ) : null}

      {selected?.type === 'webauthn' ? (
        // A security key needs no code, so there is nothing to submit — but
        // dismissing the platform prompt has to leave a way back to it, or the
        // one operator whose only factor is a key is stranded on a screen with
        // a checked radio and nothing else.
        <Button type="button" className="w-full" onClick={() => void start(selected)} disabled={busy}>
          Use your security key
        </Button>
      ) : null}
    </div>
  );
}
